import { useState } from "react";
import { portalFetch } from "../api/client";
import { usePagedPortalItems } from "../hooks/usePagedPortalItems";
import type { PortalInboxItemDto } from "../model";
import { NotificationControl } from "./NotificationControl";
import { StatusBanner } from "./StatusBanner";
export function InboxView({
	projectId,
	inboxItemId,
	pushPublicKey,
}: {
	projectId?: string;
	inboxItemId?: string;
	pushPublicKey?: string;
}) {
	const [error, setError] = useState("");
	const filters = [
		...(projectId ? [`projectId=${encodeURIComponent(projectId)}`] : []),
		...(inboxItemId && inboxItemId !== "all" ? [`inboxItemId=${encodeURIComponent(inboxItemId)}`] : []),
	];
	const { items, setItems, loading, loadingMore, failed, hasMore, loadMore } = usePagedPortalItems<PortalInboxItemDto>(
		`/api/portal/v1/inbox${filters.length ? `?${filters.join("&")}` : ""}`,
	);
	const visible = items.filter(
		(item) =>
			(!projectId || item.projectId === projectId) &&
			(!inboxItemId || inboxItemId === "all" || item.id === inboxItemId),
	);
	async function read(item: PortalInboxItemDto) {
		try {
			const updated = await portalFetch<PortalInboxItemDto>(`/api/portal/v1/inbox/${item.id}/read`, {
				method: "POST",
				body: "{}",
			});
			setItems((current) => current.map((value) => (value.id === updated.id ? updated : value)));
			setError("");
		} catch {
			setError("This update could not be marked as read. It remains in your Inbox.");
		}
	}
	return (
		<section className="inbox-panel" aria-labelledby="inbox-updates-heading">
			<div className="section-heading-row">
				<div>
					<p className="eyebrow">LATEST ACTIVITY</p>
					<h2 id="inbox-updates-heading">Updates</h2>
				</div>
				<p>Everything stays available here, even when browser notifications are off.</p>
			</div>
			<NotificationControl publicKey={pushPublicKey} />
			{(error || failed) && (
				<StatusBanner kind="error">
					{error || "Inbox updates could not be loaded. Refresh the page to try again."}
				</StatusBanner>
			)}
			{loading ? (
				<p className="compact-empty-state">Loading Inbox updates…</p>
			) : !error && !failed && visible.length === 0 ? (
				<div className="empty-state compact-empty-state">
					<strong>You’re caught up.</strong>
					<p>New review requests, decisions, and project updates will appear here.</p>
				</div>
			) : (
				<ul className="inbox-list">
					{visible.map((item) => (
						<li key={item.id} className={item.readAt ? "read" : "unread"}>
							<span>
								{item.kind}
								{item.projectName ? ` · ${item.projectName}` : ""}
							</span>
							<strong>{item.title}</strong>
							<p>{item.body}</p>
							{!item.readAt && (
								<button type="button" onClick={() => read(item)}>
									Mark read
								</button>
							)}
						</li>
					))}
				</ul>
			)}
			{hasMore && (
				<button type="button" className="secondary" disabled={loadingMore} onClick={() => void loadMore()}>
					{loadingMore ? "Loading…" : "Load more updates"}
				</button>
			)}
		</section>
	);
}
